import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type { QuoteDocument } from '@/modules/quotes/types'
import {
  snapshotEngine,
  type SnapshotValidityDays,
} from '@/services/snapshot'
import { useSnapshotStore } from '@/stores/snapshot-store'

const QUERY_KEY = ['snapshots'] as const

/**
 * useSnapshot — React bridge. Domain logic stays in SnapshotEngine.
 */
export function useSnapshot(quoteId?: string | null) {
  const queryClient = useQueryClient()
  const hydrateForQuote = useSnapshotStore((s) => s.hydrateForQuote)
  const refreshStore = useSnapshotStore((s) => s.refresh)
  const snapshots = useSnapshotStore((s) => s.snapshots)
  const selectedSnapshotId = useSnapshotStore((s) => s.selectedSnapshotId)
  const setSelectedSnapshot = useSnapshotStore((s) => s.setSelectedSnapshot)
  const timelineOpen = useSnapshotStore((s) => s.timelineOpen)
  const setTimelineOpen = useSnapshotStore((s) => s.setTimelineOpen)
  const compareOpen = useSnapshotStore((s) => s.compareOpen)
  const setCompareOpen = useSnapshotStore((s) => s.setCompareOpen)
  const compareLeftId = useSnapshotStore((s) => s.compareLeftId)
  const compareRightId = useSnapshotStore((s) => s.compareRightId)
  const compareResult = useSnapshotStore((s) => s.compareResult)
  const setComparePair = useSnapshotStore((s) => s.setComparePair)
  const runCompare = useSnapshotStore((s) => s.runCompare)
  const history = useSnapshotStore((s) => s.history)
  const permissions = useSnapshotStore((s) => s.permissions)
  const searchFilters = useSnapshotStore((s) => s.searchFilters)
  const setSearchFilters = useSnapshotStore((s) => s.setSearchFilters)
  const setPermissions = useSnapshotStore((s) => s.setPermissions)

  const listQuery = useQuery({
    queryKey: [...QUERY_KEY, quoteId],
    enabled: Boolean(quoteId),
    queryFn: () => {
      if (!quoteId) return []
      hydrateForQuote(quoteId)
      return snapshotEngine.listByQuote(quoteId)
    },
  })

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    refreshStore()
  }, [queryClient, refreshStore])

  const createFromQuote = useCallback(
    (
      quote: QuoteDocument,
      options?: {
        lockedPrice?: boolean
        notes?: string
        validUntilDays?: SnapshotValidityDays
      },
    ) => {
      const snapshot = snapshotEngine.createFromQuote(quote, options)
      if (quoteId) hydrateForQuote(quoteId)
      invalidate()
      return snapshot
    },
    [quoteId, hydrateForQuote, invalidate],
  )

  const captureOnSave = useCallback(
    (quote: QuoteDocument) => {
      const snapshot = snapshotEngine.captureIfChanged(quote)
      if (quoteId) hydrateForQuote(quoteId)
      invalidate()
      return snapshot
    },
    [quoteId, hydrateForQuote, invalidate],
  )

  const duplicate = useCallback(
    (snapshotId: string) => {
      const snapshot = snapshotEngine.duplicate(snapshotId)
      invalidate()
      return snapshot
    },
    [invalidate],
  )

  const setLocked = useCallback(
    (snapshotId: string, locked: boolean) => {
      const snapshot = snapshotEngine.setLocked(snapshotId, locked)
      invalidate()
      return snapshot
    },
    [invalidate],
  )

  const exportJson = useCallback((snapshotId: string) => {
    const payload = snapshotEngine.exportJson(snapshotId)
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${payload.snapshot.quoteCode}-${payload.snapshot.versionLabel}.json`
    anchor.click()
    URL.revokeObjectURL(url)
    return payload
  }, [])

  const exportPdf = useCallback((snapshotId: string) => snapshotEngine.exportPdf(snapshotId), [])

  const importJson = useCallback(
    async (file: File) => {
      const text = await file.text()
      const payload = JSON.parse(text) as unknown
      const snapshot = snapshotEngine.importJson(payload)
      invalidate()
      return snapshot
    },
    [invalidate],
  )

  const updateItems = useCallback(
    (leftId: string, rightId: string, itemKeys: string[] | 'all', createNewVersion: boolean) => {
      const result = snapshotEngine.updateSelectedItems(leftId, rightId, itemKeys, createNewVersion)
      invalidate()
      if (result) runCompare()
      return result
    },
    [invalidate, runCompare],
  )

  const updateItemsFromQuote = useCallback(
    (leftId: string, quote: QuoteDocument, itemKeys: string[] | 'all', createNewVersion: boolean) => {
      const result = snapshotEngine.updateSelectedFromQuote(leftId, quote, itemKeys, createNewVersion)
      invalidate()
      return result
    },
    [invalidate],
  )

  const selectedSnapshot = useMemo(
    () => snapshots.find((s) => s.id === selectedSnapshotId) ?? listQuery.data?.find((s) => s.id === selectedSnapshotId) ?? null,
    [snapshots, listQuery.data, selectedSnapshotId],
  )

  return {
    snapshots: listQuery.data ?? snapshots,
    selectedSnapshot,
    selectedSnapshotId,
    setSelectedSnapshot,
    timelineOpen,
    setTimelineOpen,
    compareOpen,
    setCompareOpen,
    compareLeftId,
    compareRightId,
    compareResult,
    setComparePair,
    runCompare,
    history,
    permissions,
    searchFilters,
    setSearchFilters,
    setPermissions,
    createFromQuote,
    captureOnSave,
    duplicate,
    setLocked,
    exportJson,
    exportPdf,
    importJson,
    updateItems,
    updateItemsFromQuote,
    isLoading: listQuery.isLoading,
    engine: snapshotEngine,
  }
}
